import { useEffect, useRef, useState } from 'react';

const reasons = [
  {
    icon: 'ri-handshake-line',
    title: 'Unrivaled Negotiating Power',
    description:
      'We leverage a robust network of established partnerships with luxury hotels and independent venues across Canada, the U.S., and key international markets. Our independence is our greatest asset; because we represent no single brand, we are free to search the entire global market to secure the most advantageous terms and contract concessions for our clients.',
    image:
      'https://public.readdy.ai/ai/img_res/220e01856148531fccb16d56edbb4264.jpg',
    stat: '500+',
    statLabel: 'Venue Partners',
  },
  {
    icon: 'ri-rocket-2-line',
    title: 'Strategic Incentive Architecture',
    description:
      "We don't just book trips; we design high-impact rewards. From Tier-1 performers to executive retreats, we specialize in creating transformational journeys that drive engagement and ROI for your most valuable human assets.",
    image:
      'https://readdy.ai/api/search-image?query=Stunning%20luxury%20private%20yacht%20deck%20with%20champagne%20glasses%20at%20sunset%20overlooking%20turquoise%20Mediterranean%20waters%2C%20warm%20golden%20light%20casting%20long%20shadows%2C%20premium%20incentive%20travel%20experience%20with%20elegant%20table%20setting%20and%20tropical%20backdrop%2C%20cinematic%20warm%20color%20grading&width=600&height=400&seq=why-choose-incentive-002&orientation=landscape',
    stat: '98%',
    statLabel: 'Client Retention',
  },
  {
    icon: 'ri-loop-left-line',
    title: 'Comprehensive Lifecycle Management',
    description:
      'Our expertise spans the entire event journey—from initial site selection and rigorous contract negotiation to on-site production and post-event analysis. Whether it is an intimate leadership summit or a large-scale international conference, we execute with a level of precision that mirrors your own corporate reputation.',
    image:
      'https://readdy.ai/api/search-image?query=Stunning%20modern%20glass%20skyscraper%20architecture%20in%20Toronto%20financial%20district%20at%20golden%20hour%20sunset%2C%20bright%20warm%20amber%20light%20reflecting%20off%20sleek%20contemporary%20CN%20Tower%20and%20downtown%20building%20facades%2C%20cinematic%20wide%20angle%20cityscape%20with%20luxury%20corporate%20towers%20and%20clear%20blue%20sky%2C%20premium%20Canadian%20business%20destination%20with%20sophisticated%20urban%20atmosphere%20and%20warm%20natural%20lighting%2C%20bright%20and%20airy%20architectural%20photography&width=600&height=400&seq=why-choose-lifecycle-toronto-007&orientation=landscape',
    stat: '1,200+',
    statLabel: 'Events Delivered',
  },
];

function useInView(threshold = 0.15) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.disconnect();
        }
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, visible };
}

function CountUp({ target, duration = 2000 }: { target: string; duration?: number }) {
  const [display, setDisplay] = useState('0');
  const ref = useRef<HTMLSpanElement>(null);
  const hasAnimated = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated.current) {
          hasAnimated.current = true;
          const numericMatch = target.match(/[\d,.]+/);
          if (!numericMatch) {
            setDisplay(target);
            return;
          }
          const prefix = target.slice(0, target.indexOf(numericMatch[0]));
          const suffix = target.slice(target.indexOf(numericMatch[0]) + numericMatch[0].length);
          const numericStr = numericMatch[0].replace(/,/g, '');
          const endVal = parseFloat(numericStr);
          const isDecimal = numericStr.includes('.');
          const hasComma = numericMatch[0].includes(',');
          const startTime = performance.now();

          const animate = (now: number) => {
            const elapsed = now - startTime;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            const current = eased * endVal;
            let formatted: string;
            if (isDecimal) {
              formatted = current.toFixed(1);
            } else {
              const rounded = Math.round(current);
              formatted = hasComma ? rounded.toLocaleString() : String(rounded);
            }
            setDisplay(`${prefix}${formatted}${suffix}`);
            if (progress < 1) requestAnimationFrame(animate);
          };
          requestAnimationFrame(animate);
          observer.disconnect();
        }
      },
      { threshold: 0.5 },
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [target, duration]);

  return <span ref={ref}>{display}</span>;
}

function FeatureCard({
  reason,
  index,
  visible,
}: {
  reason: (typeof reasons)[0];
  index: number;
  visible: boolean;
}) {
  const [hovered, setHovered] = useState(false);

  return (
    <div
      className={`group relative transition-all duration-1000 ${
        visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-20'
      }`}
      style={{
        transitionDelay: `${index * 250}ms`,
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Card */}
      <div className="relative bg-[#111111] rounded-3xl overflow-hidden border border-white/[0.06] transition-all duration-700 shadow-2xl hover:shadow-[0_30px_80px_rgba(0,0,0,0.6)] cursor-default" style={{ borderColor: hovered ? 'rgba(213,186,107,0.3)' : undefined }}>
        {/* Image area */}
        <div className="relative w-full h-[220px] overflow-hidden">
          <img
            src={reason.image}
            alt={reason.title}
            title={`MGroup Global - ${reason.title}`}
            className={`w-full h-full object-cover object-top transition-transform duration-[1200ms] ${
              hovered ? 'scale-110' : 'scale-100'
            }`}
          />
          {/* Gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#111111] via-[#111111]/60 to-transparent" />
        </div>

        {/* Content */}
        <div className="relative px-8 pb-9 pt-6">
          <h4 className="text-xl font-bold text-white mb-4 leading-snug tracking-tight">
            <a href="#about" className="transition-colors duration-300" style={{ color: 'white' }} onMouseEnter={(e) => (e.currentTarget.style.color = '#D5BA6B')} onMouseLeave={(e) => (e.currentTarget.style.color = 'white')}>
              {reason.title}
            </a>
          </h4>

          <p className="text-sm text-white/70 leading-relaxed font-light">
            {reason.description}
          </p>

          {/* Bottom accent line */}
          <div
            className={`mt-7 h-[2px] rounded-full transition-all duration-700 origin-left ${
              hovered ? 'w-full opacity-100' : 'w-0 opacity-0'
            }`}
            style={{ background: 'linear-gradient(to right, #D5BA6B, #B89A4A)' }}
          />
        </div>
      </div>
    </div>
  );
}

export default function About() {
  const story = useInView(0.15);
  const cards = useInView(0.08);
  const closing = useInView(0.2);

  return (
    <section id="about" className="relative overflow-hidden">
      {/* ── Foundation Story ── */}
      <div className="relative bg-gradient-to-br from-[#1a1a1a] via-[#141414] to-[#0d0d0d] py-24 md:py-32 overflow-hidden">
        {/* Ambient glow */}
        <div className="absolute top-0 right-0 w-[600px] h-[600px] rounded-full blur-[120px] pointer-events-none" style={{ backgroundColor: 'rgba(213,186,107,0.05)' }} />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full blur-[100px] pointer-events-none" style={{ backgroundColor: 'rgba(213,186,107,0.03)' }} />
        {/* Subtle geometric accent */}
        <div className="absolute bottom-20 left-10 w-72 h-72 rotate-45 pointer-events-none" style={{ borderWidth: '1px', borderColor: 'rgba(213,186,107,0.05)' }} />

        <div
          ref={story.ref}
          className={`relative z-10 max-w-7xl mx-auto px-6 lg:px-16 transition-all duration-1000 ${
            story.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'
          }`}
        >
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Left — Image */}
            <div className="relative group">
              <div className="absolute -inset-3 rounded-3xl blur-xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" style={{ background: 'linear-gradient(to bottom right, rgba(213,186,107,0.2), transparent, rgba(213,186,107,0.1))' }} />
              <div className="relative rounded-2xl overflow-hidden shadow-[0_40px_80px_rgba(0,0,0,0.5)]">
                <div className="w-full h-[420px] md:h-[480px]">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20luxury%20corporate%20boardroom%20with%20warm%20ambient%20lighting%2C%20sophisticated%20dark%20wood%20interior%2C%20premium%20leather%20chairs%20around%20a%20polished%20conference%20table%2C%20subtle%20golden%20accent%20lighting%2C%20high-end%20professional%20meeting%20space%20with%20floor%20to%20ceiling%20windows%20showing%20city%20skyline%20at%20dusk%2C%20cinematic%20warm%20tones%20with%20rich%20contrast&width=800&height=600&seq=about-story-boardroom-002&orientation=landscape"
                    alt="MGroup Global premium corporate environment"
                    title="MGroup Global corporate consultancy boardroom"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                {/* Gold frame accent */}
                <div className="absolute inset-0 rounded-2xl pointer-events-none" style={{ borderWidth: '1px', borderColor: 'rgba(213,186,107,0.1)' }} />
                {/* Bottom gradient overlay */}
                <div className="absolute bottom-0 left-0 right-0 h-24 bg-gradient-to-t from-black/40 to-transparent" />
              </div>
            </div>

            {/* Right — Content */}
            <div>
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-[2px]" style={{ background: 'linear-gradient(to right, #D5BA6B, #B89A4A)' }} />
                <span className="text-[11px] uppercase tracking-[0.25em] font-semibold" style={{ color: '#D5BA6B' }}>
                  Our Story
                </span>
              </div>
              <h3 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white leading-tight mb-8">
                <span style={{ color: '#D5BA6B' }}>Global</span> Reach. <span className="block">Principal-Led Precision.</span>
              </h3>
              <p className="text-base text-white/80 leading-relaxed mb-5 font-light">
                Established in 2010, MGroup Global is a premier international partner specializing in{' '}
                <strong className="text-white font-medium">Strategic Venue Sourcing</strong>,{' '}
                <strong className="text-white font-medium">End-to-End Event Management</strong>, and{' '}
                <strong className="text-white font-medium">Bespoke Incentive Travel</strong>. While we are deeply rooted in the Canadian market, our specialized network allows us to provide unrivaled access and execution across the U.S. and worldwide.
              </p>
              <p className="text-base text-white/80 leading-relaxed mb-10 font-light">
                With over 25 years of collective industry intelligence, our team operates as a
                high-performance extension of your organization. We bridge the gap between global logistics and
                personal accountability, ensuring every program—whether local or international—is executed with
                meticulous precision and measurable results.
              </p>

              {/* Stats row — dramatic */}
              <div className="grid grid-cols-2 gap-6 pt-8 border-t border-white/10">
                {[
                  { value: '25+', label: 'Years of Excellence' },
                  { value: '100%', label: 'Client-First Focus' },
                ].map((stat, idx) => (
                  <div
                    key={stat.label}
                    className={`relative text-center group cursor-default transition-all duration-1000 ${
                      story.visible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-10 scale-90'
                    }`}
                    style={{ transitionDelay: `${600 + idx * 250}ms` }}
                  >
                    {/* Glow background on hover */}
                    <div
                      className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none blur-2xl"
                      style={{ background: 'radial-gradient(circle, rgba(213,186,107,0.15) 0%, transparent 70%)' }}
                    />

                    <div className="relative py-5 px-3 rounded-2xl transition-all duration-500 group-hover:-translate-y-1">
                      {/* Number with animated shimmer */}
                      <div className="relative inline-block">
                        <div
                          className="text-3xl md:text-4xl font-bold transition-all duration-500 group-hover:scale-110"
                          style={{ color: '#D5BA6B' }}
                        >
                          <CountUp target={stat.value} duration={2200} />
                        </div>
                        {/* Shimmer sweep */}
                        <div
                          className={`absolute inset-0 pointer-events-none overflow-hidden ${
                            story.visible ? 'animate-shimmer' : ''
                          }`}
                          style={{ animationDelay: `${1800 + idx * 300}ms` }}
                        >
                          <div
                            className="absolute inset-0 -translate-x-full"
                            style={{
                              background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)',
                              animation: story.visible ? `shimmerSweep 1.5s ${1.8 + idx * 0.3}s ease-in-out forwards` : 'none',
                            }}
                          />
                        </div>
                      </div>

                      {/* Animated underline */}
                      <div className="flex justify-center mt-2 mb-3">
                        <div
                          className={`h-[2px] rounded-full transition-all duration-1000 ease-out ${
                            story.visible ? 'w-10 opacity-100' : 'w-0 opacity-0'
                          }`}
                          style={{
                            transitionDelay: `${1200 + idx * 250}ms`,
                            background: 'linear-gradient(to right, transparent, #D5BA6B, transparent)',
                          }}
                        />
                      </div>

                      {/* Label */}
                      <div
                        className={`text-[10px] sm:text-[11px] uppercase tracking-wider text-white/40 font-medium transition-all duration-700 ${
                          story.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'
                        }`}
                        style={{ transitionDelay: `${1000 + idx * 250}ms` }}
                      >
                        {stat.label}
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <a
                href="#services"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-3 mt-10 font-semibold text-sm px-7 py-3 rounded-full transition-all duration-400 cursor-pointer group"
                style={{ borderWidth: '1px', borderColor: 'rgba(213,186,107,0.4)', color: '#D5BA6B' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#D5BA6B'; e.currentTarget.style.backgroundColor = '#D5BA6B'; e.currentTarget.style.color = '#1a1a1a'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(213,186,107,0.4)'; e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#D5BA6B'; }}
              >
                Discover Our Journey
                <span className="w-8 h-8 flex items-center justify-center rounded-full transition-colors duration-300" style={{ backgroundColor: 'rgba(213,186,107,0.1)' }}>
                  <i className="ri-arrow-right-line text-base" />
                </span>
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* ── Why Choose Section — REDESIGNED ── */}
      <div className="relative bg-gray-900 py-24 md:py-28 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full blur-[150px] pointer-events-none" style={{ backgroundColor: 'rgba(213,186,107,0.03)' }} />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] rounded-full blur-[120px] pointer-events-none" style={{ backgroundColor: 'rgba(213,186,107,0.02)' }} />
        {/* Diagonal accent lines */}
        <div className="absolute top-40 left-0 w-[400px] h-px rotate-[30deg] pointer-events-none" style={{ background: 'linear-gradient(to right, rgba(213,186,107,0.1), transparent)' }} />
        <div className="absolute bottom-60 right-0 w-[300px] h-px -rotate-[20deg] pointer-events-none" style={{ background: 'linear-gradient(to left, rgba(213,186,107,0.1), transparent)' }} />
        {/* Geometric shapes */}
        <div className="absolute top-20 right-20 w-40 h-40 border border-white/[0.03] rotate-45 pointer-events-none" />
        <div className="absolute bottom-32 left-16 w-24 h-24 rotate-12 rounded-xl pointer-events-none" style={{ borderWidth: '1px', borderColor: 'rgba(213,186,107,0.05)' }} />

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-16">
          {/* Section header */}
          <div className="text-center mb-16">
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="w-12 h-[2px]" style={{ background: 'linear-gradient(to right, #D5BA6B, #B89A4A)' }} />
                <span className="text-[11px] uppercase tracking-[0.3em] font-semibold" style={{ color: '#D5BA6B' }}>
                  Our Advantage
                </span>
                <div className="w-12 h-[2px]" style={{ background: 'linear-gradient(to left, #D5BA6B, #B89A4A)' }} />
              </div>
              <h3 className="text-3xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight">
                Why Industry Leaders Choose{' '}
                <span className="block" style={{ color: '#D5BA6B' }}>MGroup Global</span>
              </h3>
          </div>

          {/* Feature cards — staggered grid */}
          <div ref={cards.ref} className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {reasons.map((reason, i) => (
              <FeatureCard key={reason.title} reason={reason} index={i} visible={cards.visible} />
            ))}
          </div>

          {/* Closing Statement */}
          <div
            className={`mt-12 pt-12 border-t border-white/[0.06] transition-all duration-1000 delay-700 ${
              cards.visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
            }`}
          >
            <div className="text-center max-w-xl mx-auto">
              <div className="w-10 h-[2px] mx-auto mb-5" style={{ backgroundColor: '#D5BA6B' }} />
              <p className="text-lg sm:text-2xl md:text-[1.75rem] font-bold text-white leading-snug mb-2 whitespace-nowrap">
                At MGroup Global, <span style={{ color: '#D5BA6B' }}>the world is our venue.</span>
              </p>
              <p className="text-sm sm:text-base md:text-lg font-light text-white/80 italic mb-6">
                We find the space, you make the impact.
              </p>
              <a
                href="#services"
                onClick={(e) => {
                  e.preventDefault();
                  document.getElementById('services')?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="inline-flex items-center gap-2 font-semibold text-sm px-6 py-2.5 rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:scale-105 whitespace-nowrap cursor-pointer"
                style={{ borderWidth: '1px', borderColor: 'rgba(213,186,107,0.4)', color: '#D5BA6B' }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#D5BA6B'; e.currentTarget.style.backgroundColor = '#D5BA6B'; e.currentTarget.style.color = '#1a1a1a'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'rgba(213,186,107,0.4)'; e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#D5BA6B'; }}
              >
                Explore Our Services
                <i className="ri-arrow-right-line" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
